import {
    type SetUpTOTPOutput,
    confirmSignIn,
    confirmUserAttribute,
    deleteUserAttributes,
    fetchAuthSession,
    fetchDevices,
    fetchMFAPreference,
    fetchUserAttributes,
    forgetDevice,
    rememberDevice,
    resendSignUpCode,
    sendUserAttributeVerificationCode,
    setUpTOTP,
    signIn,
    signInWithRedirect,
    signOut,
    updateMFAPreference,
    updateUserAttribute,
    verifyTOTPSetup,
} from "aws-amplify/auth"
import moment from "moment-timezone"
import type { NavigateFunction } from "react-router-dom"

import { COMMON_AUTH_CONFIG } from "@common-shared/config/auth.config.ts"
import { IOVPasswordRestoreProcessData } from "@common-shared/types/requestData/oneVerifyRequestData.types.ts"
import { ITokenResponseData } from "@common-shared/types/responseData/commonResponseData.types.ts"
import { IOneVerifyOrganizationUserInvite } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { BrowserStorageType } from "@front-shared/config/general.config.ts"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper.ts"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper.tsx"

import { AUTH_CONFIG, LoginStep, MFAType } from "../config/auth.config"
import { GENERAL_CONFIG } from "../config/general.config"
import { API_ROUTES, ROUTES_CONFIG } from "../config/routes.config"
import { AuthHelper } from "../helpers/auth.helper"
import { HttpHelper } from "../helpers/http.helper"
import { UtilHelper } from "../helpers/util.helper.tsx"
import type {
    IAuthCachedData,
    ILoginData,
    IProcessMFACodeData,
    IRegisterData,
} from "../types/data.types.ts"

let AUTH_CACHE: IAuthCachedData | null = null

export class AuthService {
    /**
     * Login user to app - using Amplify sdk
     */
    static async login(
        data: ILoginData,
        navigate?: NavigateFunction,
        withLogout = true,
    ): Promise<{
        result: boolean
        newLoginStep: LoginStep | null
        ignoreOnSuccessCallback: boolean
        ignoreOnErrorCallback: boolean
        mfaChallengePhoneNumber: string | null
    }> {
        let result = false
        let newLoginStep: LoginStep | null = null
        let ignoreOnSuccessCallback = false
        let ignoreOnErrorCallback = false
        let mfaChallengePhoneNumber: string | null = null

        const onMFASetupRequired = () => {
            // Need to collect user's phone number and enable mfa for account
            newLoginStep = LoginStep.MFA_SELECTION
            ignoreOnSuccessCallback = true
            ignoreOnErrorCallback = true
        }

        const onUserSignInDone = async () => {
            const needMFASetup = await AuthService.checkMFASetupNeeded()

            if (needMFASetup) {
                onMFASetupRequired()
            } else {
                result = await AuthHelper.processUserSuccessLoggedIn(navigate)
            }
        }

        // Logout just in case, controlled by argument
        // In new login flow -> need to logout
        // In resend MFA code flow -> no need to logout
        try {
            if (withLogout) {
                await signOut()
            }
        } catch (e) {}

        try {
            const singInResult = await signIn({
                username: data.email,
                password: data.password,
            })

            switch (singInResult.nextStep?.signInStep) {
                case "CONFIRM_SIGN_UP":
                    await resendSignUpCode({
                        username: data.email,
                    })

                    // Redirect to register success page with needed messaging
                    // setTimeout so it's processed in next tick
                    setTimeout(() => {
                        UtilHelper.redirectTo(
                            ROUTES_CONFIG.registerSuccessUrl +
                                `?email=${data.email}`,
                            navigate,
                        )
                    })

                    ignoreOnSuccessCallback = true
                    ignoreOnErrorCallback = true

                    break

                case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
                    // Need to collect user's code from sms and proceed with MFA challenge
                    newLoginStep = LoginStep.MFA_SMS_CODE_COLLECTION
                    ignoreOnSuccessCallback = true
                    ignoreOnErrorCallback = true

                    mfaChallengePhoneNumber =
                        singInResult.nextStep.codeDeliveryDetails
                            ?.destination || null

                    break

                case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
                    newLoginStep = LoginStep.MFA_TOTP_CODE_COLLECTION
                    ignoreOnSuccessCallback = true
                    ignoreOnErrorCallback = true

                    break

                case "DONE":
                    const isUserDeviceExpired =
                        await AuthService.checkRememberedDevicesExpired()

                    // If some device expired -> we need to init login flow again
                    if (isUserDeviceExpired) {
                        return await AuthService.login(
                            data,
                            navigate,
                            withLogout,
                        )
                    }

                    await onUserSignInDone()

                    break
            }
        } catch (e: any) {
            switch (e.name) {
                case "UserAlreadyAuthenticatedException":
                    await onUserSignInDone()

                    break

                // Don't show toast if wrong creds, it's handled by form
                case "NotAuthorizedException":
                    break

                case "LimitExceededException":
                    ToastrHelper.error(e.message)
                    ignoreOnErrorCallback = true

                    break

                case "MFAMethodNotFoundException":
                    onMFASetupRequired()

                    break

                default:
                    ToastrHelper.error(e.message)
            }
        }

        return {
            result,
            newLoginStep,
            ignoreOnSuccessCallback,
            ignoreOnErrorCallback,
            mfaChallengePhoneNumber,
        }
    }

    /**
     * Create a demo login session.
     */
    static async demoLogin(
        email: string,
    ): Promise<ITokenResponseData | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.AUTH_DEMO_LOGIN,
            { email },
            "POST",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Set up TOTP - using Amplify
     * Returns data required for TOTP setup; link for QR code and shared secret
     */
    static async setUpTOTP(): Promise<SetUpTOTPOutput | null> {
        try {
            return await setUpTOTP()
        } catch (e) {
            ToastrHelper.error(
                "Failed to initiate Auth App integration. Please try again or contact support.",
            )

            return null
        }
    }

    /**
     * Update phone number of a user - using Amplify
     * User needs to be authenticated with Amplify
     */
    static async updatePhoneNumber(phoneNumber: string): Promise<boolean> {
        try {
            await updateUserAttribute({
                userAttribute: {
                    attributeKey: "phone_number",
                    value: phoneNumber,
                },
            })

            await sendUserAttributeVerificationCode({
                userAttributeKey: "phone_number",
            })

            return true
        } catch (e: any) {
            switch (e.name) {
                case "InvalidParameterException":
                    ToastrHelper.error(
                        "Please provide valid phone number. It should be in international format. Example: +1234567890",
                    )

                    return false
            }

            console.error(e)

            ToastrHelper.error(
                "Failed to update phone number. Please try again or contact support.",
            )

            return false
        }
    }

    /**
     * Process MFA code - using Amplify
     * Null means that auto re-login is needed
     */
    static async processMFACode(
        data: IProcessMFACodeData,
        onSessionExpired?: () => void,
    ): Promise<boolean | null> {
        try {
            // User is considered logged in Amplify if in MFA setup flow
            const isSetupFlow = await AuthService.isLoggedIn()

            if (isSetupFlow) {
                switch (data.mfaType) {
                    case MFAType.TOTP:
                        await verifyTOTPSetup({
                            code: data.code,
                        })

                        break
                    case MFAType.SMS:
                        await confirmUserAttribute({
                            userAttributeKey: "phone_number",
                            confirmationCode: data.code,
                        })

                        break
                }

                await updateMFAPreference({
                    ...(data.mfaType === MFAType.SMS
                        ? { sms: "PREFERRED" }
                        : {}),
                    ...(data.mfaType === MFAType.TOTP
                        ? { totp: "PREFERRED" }
                        : {}),
                })
            } else {
                await confirmSignIn({
                    challengeResponse: data.code,
                })

                // here need to check browser storage and set phone number to null in cognito if force reset MFA
                // Then trigger login action again
                // It will auto login again in password.step component and will show user MFA setup form

                const needForceResetMFA = BrowserStorageHelper.get(
                    GENERAL_CONFIG.browserStorageKeys.authForceResetMFA,
                    undefined,
                    BrowserStorageType.sessionStorage,
                )

                BrowserStorageHelper.remove(
                    GENERAL_CONFIG.browserStorageKeys.authForceResetMFA,
                    BrowserStorageType.sessionStorage,
                )

                if (needForceResetMFA && data.withLoggingIn) {
                    // Disable MFA for user -> it's required for removing phone number from user
                    await updateMFAPreference({
                        sms: "DISABLED",
                        totp: "DISABLED",
                    })

                    // Remove current phone number from user
                    await deleteUserAttributes({
                        userAttributeKeys: ["phone_number"],
                    })

                    return null
                }

                if (data.rememberDevice) {
                    // Remembering device fail shouldn't fail MFA process
                    try {
                        await rememberDevice()
                    } catch (e) {
                        console.error(e)
                    }
                }
            }

            return data.withLoggingIn
                ? await AuthHelper.processUserSuccessLoggedIn()
                : true
        } catch (e: any) {
            switch (e.name) {
                case "CodeMismatchException":
                    ToastrHelper.error(
                        "Invalid code provided. Please try again",
                    )

                    break

                case "NotAuthorizedException":
                    ToastrHelper.error(
                        "Your session has expired. Please start the flow from the beginning",
                    )

                    onSessionExpired?.()

                    break

                default:
                    console.error(e)

                    ToastrHelper.error(
                        "Failed to process verification code. Please try again or contact support",
                    )
            }

            return false
        }
    }

    /**
     * Resend MFA code - using Amplify
     * For not authenticated users (when doing challenge during login) -> will hit login endpoint again, that will trigger resend
     * For authenticated users (when doing setup) -> will trigger directly
     */
    static async resendMfaCode(loginData: ILoginData | null): Promise<boolean> {
        try {
            // User is considered logged in Amplify if in MFA setup flow
            const isSetupFlow = await AuthService.isLoggedIn()

            if (isSetupFlow) {
                await sendUserAttributeVerificationCode({
                    userAttributeKey: "phone_number",
                })
            } else {
                if (!loginData) {
                    return false
                }

                await AuthService.login(loginData, undefined, false)
            }

            return true
        } catch (e: any) {
            return false
        }
    }

    /**
     * Register user in application - using backend
     */
    static async register(data: IRegisterData): Promise<any> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.AUTH_REGISTER,
            {
                ...(data.emailData
                    ? {
                          email: data.emailData.email,
                      }
                    : {}),
                ...(data.passwordData || {}),

                inviteToken: data.inviteToken,
            },
            "post",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Initiate forgot password flow - using backend
     */
    static async forgotPassword(email: string): Promise<any> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.AUTH_FORGOT_PASSWORD,
            {
                email,
            },
            "post",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Process forgot password flow - using backend
     */
    static async forgotPasswordProcess(
        data: IOVPasswordRestoreProcessData,
    ): Promise<{ forgotPasswordFlowDone: boolean } | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.AUTH_FORGOT_PASSWORD_PROCESS,
            data,
            "post",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Check if email is available - using backend (same route as register flow /
     * `authCheckEmailAvailable`).
     *
     * @param ignoreEmailTakenErrorToast When true, a taken email (400) does not show an
     *        error toast — useful for flows that only need the boolean outcome.
     */
    static async emailAvailable(
        email: string,
        options?: { ignoreEmailTakenErrorToast?: boolean },
    ): Promise<any> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.AUTH_EMAIL_AVAILABLE,
            {
                email,
            },
            "post",
            {},
            undefined,
            GENERAL_CONFIG.defaultToastrTimeout,
            options?.ignoreEmailTakenErrorToast ? [400] : [],
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Resend verification link to just registered user - using backend
     */
    static async resendVerificationLink(email: string): Promise<any> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.AUTH_RESEND_VERIFY_RESEND,
            {
                email,
            },
            "post",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Confirm user by email and verification code in Cognito - using backend
     */
    static async confirmUser(
        email: string,
        confirmationCode: string,
    ): Promise<any> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.AUTH_CONFIRM_USER,
            {
                email,
                confirmationCode,
            },
            "post",
            undefined,
            undefined,
            GENERAL_CONFIG.extendedToastrTimeout,
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Check invite token of a user - using backend
     */
    static async checkInviteToken(
        inviteToken: string,
    ): Promise<IOneVerifyOrganizationUserInvite | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.AUTH_CHECK_INVITE_TOKEN.replace(
                ":inviteToken",
                inviteToken,
            ),
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Check if user's remembered devices expired
     * We need to implement custom logic for this (based on our rules of expiration), since Cognito doesn't support it out of the bog
     * And then if expired -> we'll forget it and init login flow again
     */
    static async checkRememberedDevicesExpired(
        forceExpireAll = false,
    ): Promise<boolean> {
        try {
            if (!AuthService.isMFAActivatedForEnvironment()) {
                return false
            }

            // Will fail if user is not logged in
            const devices = await fetchDevices()

            let hasExpiredDevices = false

            for (const device of devices) {
                const isExpired = forceExpireAll
                    ? true
                    : device.createDate
                      ? moment(device.createDate)
                            .add(
                                AUTH_CONFIG.rememberedDeviceExpirationDays,
                                "days",
                            )
                            .toDate()
                            .getTime() < new Date().getTime()
                      : false

                if (!isExpired) {
                    continue
                }

                hasExpiredDevices = true

                await forgetDevice({
                    device,
                })
            }

            return hasExpiredDevices
        } catch (e) {
            return false
        }
    }

    /**
     * Check if the current user is a federated user (SSO) based on id token
     */
    static async isFederatedUser(): Promise<boolean> {
        try {
            const session = await fetchAuthSession()

            const idToken = session.tokens?.idToken

            if (!idToken) {
                return false
            }

            // Decode the ID token to check for federated indicators
            const payload = idToken.payload

            // Check for federated login indicators:
            // The identities field indicates federated login
            return !!payload[COMMON_AUTH_CONFIG.identitiesAttributeKey]
        } catch (e) {
            return false
        }
    }

    /**
     * Check if user needs MFA setup
     * We handle this requirement on the code level
     * Since to handle it on the Cognito level we need to require phone number during sign up
     * There's no way easily update it later if user is not authenticated
     * And in order to authenticate, MFA is required (phone number), so it's going in circles
     */
    static async checkMFASetupNeeded(ignoreCache = true): Promise<boolean> {
        // Skip MFA setup check for federated users
        if (await AuthService.isFederatedUser()) {
            return false
        }

        // Skip MFA setup check for demo sessions
        if (AuthHelper.isDemoSessionActive()) {
            return false
        }

        try {
            if (!AuthService.isMFAActivatedForEnvironment()) {
                return false
            }

            const mfaPreferences = ignoreCache
                ? await fetchMFAPreference()
                : (await AuthService.getAuthCache())?.fetchedMFAPreference

            if (!mfaPreferences.preferred) {
                // If preferred is not set, it means MFA is not set up
                return true
            }

            if (mfaPreferences.preferred === MFAType.TOTP) {
                // If TOTP is preferred, we don't need to check anything else
                return false
            }

            const userAttributes = ignoreCache
                ? await fetchUserAttributes()
                : (await AuthService.getAuthCache())?.fetchedUserAttributes

            // Checking that mfaPreferences.preferred is not enough for phone number
            // we add a check to ensure the phone number is configured for extra safety
            return (
                mfaPreferences.preferred === MFAType.SMS &&
                (!userAttributes.phone_number ||
                    !userAttributes.phone_number_verified)
            )
        } catch (e) {
            return true
        }
    }

    /**
     * Get auth cache object -> if expired, regenerate
     */
    static async getAuthCache(triesCount = 1): Promise<IAuthCachedData> {
        if (
            AUTH_CACHE &&
            new Date().getTime() - AUTH_CACHE.createdAt < AUTH_CONFIG.cacheTtlMs
        ) {
            return AUTH_CACHE
        }

        try {
            AUTH_CACHE = {
                fetchedMFAPreference: await fetchMFAPreference(),
                fetchedUserAttributes: await fetchUserAttributes(),
                isFederatedUser: await AuthService.isFederatedUser(),
                createdAt: new Date().getTime(),
            }

            return AUTH_CACHE
        } catch (e) {
            // Retry getting auth cache if it failed
            // Sometimes Cognito just throttles requests
            if (triesCount < AUTH_CONFIG.cacheMaxTries) {
                await UtilHelper.sleep(2 * triesCount)

                return AuthService.getAuthCache(triesCount + 1)
            }

            throw e
        }
    }

    /**
     * Clear auth cache object
     */
    static clearAuthCache() {
        AUTH_CACHE = null
    }

    /**
     * Check if user is logged in
     */
    static async isLoggedIn(): Promise<boolean> {
        try {
            // WIll fail if user is not logged in
            await fetchUserAttributes()

            return true
        } catch (e) {
            return false
        }
    }

    /**
     * Check if MFA is activated for environment
     */
    static isMFAActivatedForEnvironment(): boolean {
        return GENERAL_CONFIG.isProduction || GENERAL_CONFIG.isStaging
    }

    /**
     * Resolve identity provider
     */
    static async checkAuthFlowIdentityProvider(email: string): Promise<any> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.AUTH_CHECK_AUTH_FLOW_IDENTITY_PROVIDER,
            { email },
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Federated sign-in with custom provider
     */
    static async federatedSignInWithProvider(provider: string): Promise<void> {
        try {
            await signInWithRedirect({ provider: { custom: provider } })
        } catch (error) {
            console.error("Federated sign-in with provider error:", error)

            throw error
        }
    }
}
